import React from "react";
import { COMPANY } from "@/lib/company";
import { AnimatedBrandMark } from "@/components/shared/AnimatedBrandMark";
import type { BrandLogoSlot } from "@/hooks/useBrandLogos";

/**
 * Renders the SociBaba wordmark with the trademark ™ superscript.
 *
 * Use this everywhere the app logo/brand appears as a header, sidebar item,
 * or splash element. When `showIcon` is set the mark is rendered as the
 * animated logo (playful Chase / Dig / Search / Shuffle moods, with the
 * admin-uploadable icon per variant). Body-text mentions stay plain.
 */
interface BrandMarkProps {
  className?: string;
  markClassName?: string;
  as?: keyof JSX.IntrinsicElements;
  /** When true, renders the animated icon + wordmark logo. */
  showIcon?: boolean;
  /** Tailwind classes for the icon (size / margin / dark-mode filter). */
  iconClassName?: string;
  /** Which uploaded icon slot to use — app (default), shop, or score. */
  variant?: BrandLogoSlot;
  /** Plain-text suffix rendered after Baba (e.g. "SIBA" for /shop). */
  suffix?: string;
  /** "onDark" forces the calm 'Soci' text to white regardless of app theme. */
  tone?: "auto" | "onDark";
  /**
   * `quiet` mutes the periodic idle animation (shuffle / glitch flashes) so
   * the mark stays visually stable inside dense chrome like the sticky Shop
   * header. The initial greeting + hover interactions still play.
   */
  quiet?: boolean;
}

export const BrandMark: React.FC<BrandMarkProps> = ({
  className,
  markClassName = "text-[0.5em] align-super ml-[1px] opacity-70 font-normal",
  as: Tag = "span",
  showIcon = false,
  iconClassName = "h-[1.15em] w-[1.15em] -mt-[2px] mr-2 inline-block align-middle dark:invert",
  variant = "app",
  suffix,
  tone = "auto",
  quiet = false,
}) => {
  if (showIcon) {
    return (
      <AnimatedBrandMark
        className={className}
        markClassName={markClassName}
        showIcon
        iconClassName={iconClassName}
        variant={variant}
        suffix={suffix}
        tone={tone}
        quiet={quiet}
      />
    );
  }
  return (
    <Tag className={className}>
      <span className="align-middle">{COMPANY.brand}</span>
      <sup className={markClassName} aria-label="trademark">
        {COMPANY.brandMark}
      </sup>
      {suffix ? (
        <span className="align-middle inline-block ml-1.5 font-semibold text-[color:var(--sb-saffron,#f97316)] text-[0.78em] tracking-wide">
          {suffix}
        </span>
      ) : null}
    </Tag>
  );
};

export default BrandMark;
